<script setup lang="ts">
import { type VideoPlayer, videoPlayer } from "cloudinary-video-player";
import { computed, onBeforeUnmount, onMounted, ref, useId, watch } from "vue";
import { CLOUDINARY_CLOUD_NAME, CLOUDINARY_VIDEO_EXTENSION_TYPES } from "@/constants.ts";
import { convertToMp4, videoPosterUrl } from "@/util/cloudinary.js";
import type { Props } from "./types.ts";
import "cloudinary-video-player/cld-video-player.min.css";

defineOptions({
  name: "CloudinaryVideo",
});

const props = withDefaults(defineProps<Props>(), {
  src: "",
});

// Unique per instance: video.js binds to a DOM id, so two players would fight over one element.
const playerElementId = `cloudinary-video-${useId()}`;
const player = ref<VideoPlayer>();

// Bound to the element, not the player's posterOptions, which expects a publicId not a secure_url.
// The section's chosen image wins; the video's own still frame is the fallback.
// biome-ignore lint/correctness/noUnusedVariables: bound in the template, which Biome does not see
const posterSource = computed<string | undefined>(() => {
  if (props.poster) {
    return props.poster;
  }

  return videoPosterUrl(props.src);
});

// Set on the element itself, so the native player still works if video.js fails to take over.
// biome-ignore lint/correctness/noUnusedVariables: bound in the template, which Biome does not see
const fallbackSource = computed<string | undefined>(() => {
  if (!props.src) {
    return undefined;
  }

  return convertToMp4(props.src);
});

const applySource = (): void => {
  if (!player.value || !props.src) {
    return;
  }

  player.value.source(convertToMp4(props.src));
};

onMounted(() => {
  player.value = videoPlayer(playerElementId, {
    cloud_name: CLOUDINARY_CLOUD_NAME,
    secure: true,
    seekThumbnails: false,
    fluid: true,
    sourceTypes: CLOUDINARY_VIDEO_EXTENSION_TYPES,
  });

  applySource();
});

watch(() => props.src, applySource);

onBeforeUnmount(() => {
  if (player.value) {
    player.value.dispose();
    player.value = undefined;
  }
});
</script>

<template>
  <div class="w-full">
    <video
      :id="playerElementId"
      :src="fallbackSource"
      :poster="posterSource"
      :aria-label="label"
      controls
      preload="metadata"
      class="w-full h-full"
      data-testid="cloudinary-video"
    ></video>
  </div>
</template>
